<template>
  <page-scaffold
      :loading="isLoading"
      :header="headerProps"
      @profile="goToProfile"
      @support="goToSupport"
      @notifications="goToNotifications"
  >
    <template #default>
      <div :class="mainCss">
        <div class="title" style="grid-area: title-balance">Мой баланс</div>
        <div class="title" style="grid-area: title-tariffs">Мои тарифы и услуги</div>
        <div class="title" v-if="!this.isPhysical" style="grid-area: title-documents">Документы</div>
        <div class="title" style="grid-area: title-offers">Специальные предложения</div>
        <div class="balance">
          <widget-balance-skeleton v-if="isLoading" />
          <widget-balance-main
              v-else
              :number="(account && account.personalAccount) ? account.personalAccount : ''"
              :recommend-pay="balanceProps.recommendPay"
              :balance="balanceProps.balance"
              :bonus="balanceProps.bonus"
              :is-phys="isPhysical"
              :is-balance-insufficient="balanceProps.isInsufficient"
              :auto-payment-active="balanceProps.autoPaymentActive"
              :auto-payment-status="balanceProps.autoPaymentStatus"
              :payments-active="balanceProps.paymentsActive"
              :service-status="myServiceCard ? myServiceCard.status : null"
              @update="getAccount"
          />
        </div>
        <div class="tariffs">
          <widget-tariffs-skeleton v-if="isLoading" />
          <widget-tariffs-main
              v-else
              :is-legal="!isPhysical"
              :my-service="myServiceCard"
              :service-states="serviceStates"
              :chat-active="headerProps.chatActive"
              @update="getAccount"
          />
        </div>
        <div class="offers" :class="{ 'offers--no-scroll': isLoading }">
          <widget-special-offers-skeleton v-if="isLoading" />
          <template v-else>
            <item-special-offer
                v-for="offer in offers"
                :key="offer.id"
                :image="offer.imageUrl"
                :url="offer.linkUrl"
            />
          </template>
        </div>
        <div class="documents" v-if="!this.isPhysical">
          <outlined-button-with-icon @click="
        this.$router.push({name: 'Documents', query: {'page': 0}})"
                                     :icon="require('@/assets/ic_reconciliation_report.svg')" :title="'Акт сверки'"/>
          <outlined-button-with-icon @click="
        this.$router.push({name: 'Documents', query: {'page': 1}})"
                                     :icon="require('@/assets/ic_dollar.svg')"
                                     :title="'Счет на оплату'"/>
          <outlined-button-with-icon @click="
        this.$router.push({name: 'Documents', query: {'page': 2}})"
                                     :icon="require('@/assets/ic_certificate_completed_works.svg')"
                                     :title="'Акт выполненных работ'"/>
        </div>
      </div>
    </template>
  </page-scaffold>

  <widget-agreement-modal
      :visible="showAgreementModal"
      :loading="isAgreementSubmitting"
      @confirm="confirmAgreement"
  />

</template>

<script>
import OutlinedButtonWithIcon from "./outlined-button-with-icon";
import PageScaffold from "./page-scaffold";
import WidgetBalanceMain from "./widget-balance-main";
import WidgetBalanceSkeleton from "./widget-balance-skeleton";
import WidgetTariffsMain from "./widget-tariffs-main";
import WidgetTariffsSkeleton from "./widget-tariffs-skeleton";
import WidgetAgreementModal from "./widget-agreement-modal";
import ItemSpecialOffer from "./item-special-offer";
import WidgetSpecialOffersSkeleton from "./widget-special-offers-skeleton";
import axios from "axios";
import {normalizeAutopaymentStatus, resolveAutopaymentStatus} from "../utils/autopayment";
import {handleAuthFailure, saveAccountServices, syncActiveAccountFromLegacy} from "../utils/multi-account";

const SERVICE_LABELS = {
  Internet: 'Интернет',
  Tv: 'Телевидение',
  Vacation: 'Опция "В отпуске"',
  videoSurveillance: 'Видеонаблюдение',
  staticIP: 'Статический IP',
  smartHome: 'Умный дом',
  Mobile: 'SIM-карты'
};

export default {
  name: "page-main",
  components: {
    PageScaffold,
    ItemSpecialOffer,
    WidgetTariffsMain,
    WidgetTariffsSkeleton,
    WidgetSpecialOffersSkeleton,
    WidgetBalanceMain,
    WidgetBalanceSkeleton,
    WidgetAgreementModal,
    OutlinedButtonWithIcon,
  },
  data: () => {
    return {
      isLoading: true,
      isPhysical: true,
      mainCss: 'main-page-phys',
      services: {
        staticIP: "notActive",
        videoSurveillance: "processing",
        smartHome: "active"
      },
      isTv: false,
      isNet: false,
      account: null,
      accountV3: null,
      offers: [],
      autopaymentStatus: null,
      showAgreementModal: false,
      isAgreementSubmitting: false
    }
  },
  computed: {
    headerProps() {
      const accountV3 = this.accountV3;
      const account = this.account || {};
      const services = (accountV3 && accountV3.services) || (account.services) || {};

      const chatStatus = typeof services.chat === 'string' ? services.chat : null;

      return {
        number: accountV3 && accountV3.agreementNumber ? accountV3.agreementNumber : (account.agreementNumber || ''),
        chatBadge: Boolean(accountV3 && accountV3.hasNewMessages !== undefined ? accountV3.hasNewMessages : account.hasNewMessages),
        notificationBadge: Boolean(accountV3 && accountV3.hasNewNotifications !== undefined ? accountV3.hasNewNotifications : account.hasNewNotifications),
        chatActive: chatStatus === null ? true : this.isStatusActive(chatStatus)
      }
    },
    balanceProps() {
      const account = this.account || {};
      const accountV3 = this.accountV3;

      const balance = accountV3 && accountV3.balance !== undefined ? accountV3.balance : account.balance;
      const recommendPay = accountV3 && accountV3.recommendPay !== undefined ? accountV3.recommendPay : account.recommendPay;
      const bonus = accountV3 && accountV3.bonus !== undefined ? accountV3.bonus : account.bonus;

      const balanceValue = this.parseToNumber(balance);
      const totalCostValue = this.parseToNumber(accountV3 && accountV3.totalCost !== undefined ? accountV3.totalCost : account.totalCost);

      const paymentsStatus = accountV3 && accountV3.services ? accountV3.services.payments : account.services && account.services.payments;
      const serviceAutopaymentStatus = accountV3 && accountV3.services ? accountV3.services.autopayments : account.services && account.services.autopayments;
      const normalizedAutopaymentStatus = resolveAutopaymentStatus(this.autopaymentStatus, serviceAutopaymentStatus, 'disabled');

      return {
        balance: balance ?? null,
        recommendPay: recommendPay ?? null,
        bonus: bonus ?? null,
        isInsufficient: balanceValue !== null && totalCostValue !== null ? balanceValue < totalCostValue : false,
        autoPaymentStatus: normalizedAutopaymentStatus,
        autoPaymentActive: normalizedAutopaymentStatus === 'active',
        paymentsActive: paymentsStatus == null ? true : this.isStatusActive(paymentsStatus)
      }
    },
    myServiceCard() {
      const accountV3 = this.accountV3;
      if (!accountV3 || !Array.isArray(accountV3.myServices) || accountV3.myServices.length === 0) {
        return null;
      }

      const primary = accountV3.myServices[0];
      const services = Array.isArray(primary.items) ? primary.items : [];
      const firstServiceType = services.length > 0 ? services[0].type : null;
      const normalizedStatus = typeof primary.status === 'string' ? primary.status.trim().toLowerCase() : '';
      const typeLabel = firstServiceType && SERVICE_LABELS[firstServiceType]
          ? SERVICE_LABELS[firstServiceType]
          : (this.account && this.account.internetTariffs && this.account.internetTariffs.length > 0
              ? 'Интернет'
              : 'Мои услуги');

      const totalCostSource = accountV3.totalCost !== undefined ? accountV3.totalCost : this.account?.totalCost;
      const chargeAmountValue = this.parseToNumber(totalCostSource);
      const chargeAmount = chargeAmountValue === null
          ? (totalCostSource !== undefined ? `${totalCostSource} ₽` : '- ₽')
          : `${this.formatCurrency(chargeAmountValue)} ₽`;

      return {
        type: typeLabel,
        status: primary.status,
        title: primary.title,
        services,
        chargeAmount,
        chargeDate: accountV3.nextPaymentDate || this.account?.nextPaymentDate || '',
        isBalanceInsufficient: normalizedStatus === 'notactive' && this.balanceProps.isInsufficient
      };
    },
    serviceStates() {
      const services = (this.accountV3 && this.accountV3.services) || (this.account && this.account.services) || {};
      const resolveAvailability = (status) => {
        if (status === undefined || status === null) {
          return true;
        }
        return this.isStatusActive(status);
      };
      return {
        staticIpActive: resolveAvailability(services.staticIP),
        videoActive: resolveAvailability(services.videoSurveillance),
        smartHouseActive: resolveAvailability(services.smartHome),
        vacationActive: resolveAvailability(services.vacation),
        mobileActive: resolveAvailability(services.mobile)
      };
    }
  },
  methods: {
    parseToNumber(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const normalized = String(value).replace(/[^0-9,.-]/g, '').replace(',', '.')
      const parsed = parseFloat(normalized)
      return Number.isNaN(parsed) ? null : parsed
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    isStatusActive(status) {
      if (!status) {
        return false
      }
      return String(status).toLowerCase() === 'active'
    },
    persistAccountServices(services) {
      saveAccountServices(services)
    },
    async handleAgreementRequirement(accountV3) {
      let needConfirmation = null
      if (accountV3 && Object.prototype.hasOwnProperty.call(accountV3, 'needConfirmation')) {
        const flag = accountV3.needConfirmation
        if (typeof flag === 'boolean') {
          needConfirmation = flag
        }
      }

      if (needConfirmation === null) {
        needConfirmation = localStorage.agreementAccepted !== 'true'
      }

      const headers = {Authorization: `Bearer ${localStorage.token}`}
      try {
        const response = await axios({
          method: "GET",
          url: "v1/user-agreement",
          headers,
        })
        const agreed = response && response.data && response.data.agreed_to_terms === true
        localStorage.agreementAccepted = agreed ? 'true' : 'false'
        syncActiveAccountFromLegacy()
        this.showAgreementModal = !agreed
      } catch (error) {
        this.showAgreementModal = needConfirmation
      }
    },
    confirmAgreement() {
      if (this.isAgreementSubmitting) {
        return
      }
      this.isAgreementSubmitting = true
      const headers = {Authorization: `Bearer ${localStorage.token}`}
      axios({
        method: "POST",
        url: "v1/user-agreement",
        headers,
        data: {
          agreed_to_terms: true
        }
      }).then(() => {
        localStorage.agreementAccepted = 'true'
        syncActiveAccountFromLegacy()
        this.showAgreementModal = false
      }).catch(error => {
        if (error && error.response && error.response.status === 401) {
          handleAuthFailure({router: this.$router, reloadOnSwitch: true})
        }
      }).finally(() => {
        this.isAgreementSubmitting = false
      })
    },
    openSpeedTest() {
      window.open('https://2ip.ru/', '_blank')
    },
    goToProfile() {
      this.$router.push('/profile')
    },
    goToSupport() {
      if (!this.headerProps.chatActive) {
        return
      }
      this.$router.push('/chat')
    },
    goToNotifications() {
      this.$router.push('/notifications')
    },
    async getAccount() {
      this.isLoading = true
      if (localStorage.userType) {
        this.isPhysical = localStorage.userType === "individual"
      }
      if (localStorage.mainCss) {
        this.mainCss = localStorage.mainCss
      }

      const headers = {Authorization: `Bearer ${localStorage.token}`}

      try {
        const response = await axios({
          method: "GET",
          url: "v3/account",
          headers,
        })

        const v3Data = response && response.data ? response.data : null
        const accountV3 = v3Data && (v3Data.accountData ?? v3Data.account) ? (v3Data.accountData ?? v3Data.account) : null

        this.accountV3 = accountV3
        this.account = accountV3
        this.autopaymentStatus = null

        this.persistAccountServices(accountV3 && accountV3.services ? accountV3.services : null)

        if (accountV3) {
          const agreementNumber = accountV3.agreementNumber
          if (agreementNumber !== undefined) {
            localStorage.agreementNumber = agreementNumber || ''
          }

          const personalAccount = accountV3.personalAccount
          if (personalAccount !== undefined) {
            localStorage.personalAccount = personalAccount || ''
          }

          const hasNewMessages = accountV3.hasNewMessages
          if (typeof hasNewMessages === 'boolean') {
            localStorage.chatBadge = hasNewMessages.toString()
          }

          const hasNewNotifications = accountV3.hasNewNotifications
          if (typeof hasNewNotifications === 'boolean') {
            localStorage.notificationBadge = hasNewNotifications.toString()
          }

          const services = accountV3.services || {}
          if (services.chat !== undefined) {
            localStorage.chatActive = (services.chat === 'active').toString()
          }

          const userType = accountV3.type || accountV3.accountType || accountV3.customerType
          if (typeof userType === 'string') {
            const normalizedType = userType === 'legal' ? 'legalEntity'
                : userType === 'physical' ? 'individual'
                    : userType
            localStorage.userType = normalizedType
            if (normalizedType === "individual") {
              this.isPhysical = true
              localStorage.mainCss = "main-page-phys"
              this.mainCss = "main-page-phys"
            } else if (normalizedType === "legalEntity") {
              this.isPhysical = false
              localStorage.mainCss = "main-page"
              this.mainCss = "main-page"
            }
          }

          const userName = accountV3.userName || accountV3.fullName || accountV3.name
          if (typeof userName === 'string') {
            localStorage.userName = userName
          }

          const userId = accountV3.userID || accountV3.userId || (accountV3.user && accountV3.user.id)
          if (userId) {
            localStorage.userID = userId
          }

          if (Array.isArray(accountV3.televisionTariffs)) {
            this.isTv = accountV3.televisionTariffs.length !== 0
          }

          if (Array.isArray(accountV3.internetTariffs)) {
            this.isNet = accountV3.internetTariffs.length !== 0
          }
        }

        syncActiveAccountFromLegacy()

        const autopaymentUserId = (this.accountV3 && (this.accountV3.userID || this.accountV3.userId)) || localStorage.userID || null

        const [offersResult, autopaymentResult] = await Promise.allSettled([
          axios({
            method: "GET",
            url: "v1/advertisingOffers",
            headers,
          }),
          autopaymentUserId
              ? axios({
                method: "GET",
                url: "v1/autopayment",
                headers,
                params: {userID: autopaymentUserId}
              })
              : Promise.resolve(null)
        ])

        if (offersResult.status === 'fulfilled') {
          this.offers = offersResult.value.data
        }

        if (autopaymentResult && autopaymentResult.status === 'fulfilled' && autopaymentResult.value) {
          const autopaymentResponse = autopaymentResult.value.data && autopaymentResult.value.data.response
          const status = autopaymentResponse && autopaymentResponse.status
          this.autopaymentStatus = normalizeAutopaymentStatus(status)
        }
      } catch (error) {
        if (error && error.response && error.response.status === 401) {
          handleAuthFailure({router: this.$router, reloadOnSwitch: true})
        }
      } finally {
        await this.handleAgreementRequirement(this.accountV3)
        this.isLoading = false
      }
    }
  },
  mounted() {
    this.getAccount()


  },
}
</script>

<style scoped>


.main-page {
  padding-left: 24px;
  padding-right: 24px;
  width: calc(100% - 48px);
  height: 100%;
  display: grid;
  overflow-y: scroll;
  /*grid-template-columns: minmax(100px, 1fr) 1.7fr 1fr;*/
  grid-template-columns: minmax(280px, 1fr) minmax(360px, 1.6fr) minmax(260px, 1fr);
  grid-template-rows: max-content max-content max-content max-content 1fr;
  grid-template-areas:
      "title-balance title-tariffs title-documents"
      "balance tariffs documents"
      "balance tariffs title-offers"
      "balance tariffs offers"
      "buttons tariffs offers";
}

.main-page-phys {
  padding-left: 24px;
  padding-right: 24px;
  width: calc(100% - 36px);
  height: 100%;
  display: grid;
  overflow-y: scroll;
  /*grid-template-columns: minmax(100px, 1fr) 1.7fr 1fr;*/
  grid-template-columns: minmax(280px, 1fr) minmax(360px, 1.6fr) minmax(260px, 1fr);
  grid-template-rows: max-content max-content max-content max-content 1fr;
  grid-template-areas:
      "title-balance title-tariffs title-offers"
      "balance tariffs offers"
      "balance tariffs offers"
      "balance tariffs offers"
      "buttons tariffs offers";
}

.title {

  font-family: "Century Gothic", serif;
  font-weight: bold;
  color: #2D666E;
  grid-area: title;
  text-align: start;
  align-self: end;
  justify-self: start;
  /*white-space: nowrap;*/
  font-size: 1.4rem;
  margin-top: 24px;
  margin-bottom: 24px;
}

.balance {
  grid-area: balance;
  padding-right: 16px;
}

.tariffs {
  grid-area: tariffs;
}

.offers {
  overflow-y: auto;
  grid-area: offers;
  padding-left: 16px;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.offers::-webkit-scrollbar {
  width: 4px;
}

.offers::-webkit-scrollbar-thumb {
  background: transparent;
}

.offers--no-scroll {
  overflow-y: hidden;
}


.documents {
  padding: 0 16px 0 0;
  grid-area: documents;
}

.buttons {
  padding: 32px 16px 16px 0;
  grid-area: buttons;
}


.page-scaffold {
  background: #FAFDFF;
  position: absolute;
  top: 0;
  left: 0;
  overflow-y: scroll;
  height: 100vh;
  width: 100vw;
  display: grid;
  grid-template-columns: 1fr minmax(auto, 1400px) 1fr;
  grid-template-rows: max-content 1fr;
  grid-template-areas:
      "safe-zone-l top-bar safe-zone-r"
      "safe-zone-l content safe-zone-r";
}

.page-scaffold .safe-zone {
}

.page-scaffold .content {
}

</style>
